import React from 'react'
import { View, Platform } from 'react-native'
import { Input, Text } from '@ui-kitten/components'
import { Controller } from 'react-hook-form'
import StyledTextInput from './StyledTextInput'
import { useTranslation } from 'react-i18next'

export default function FormTextInput({
  control,
  name,
  label,
  placeholder = '',
  style,
  keyboardType = 'default',
  returnKeyType = 'done',
  autoCapitalize = 'none',
  textTransform,
  onChangeText: customOnChange,
  rightAccessory,
  labelAccessory,
  // Number input specific props
  required = false,
  isNumberInput = false,
  allowDecimals = true,
  min,
  max,
  defaultValue = '',
  rules,
  testID,
  ...props
}) {
  const { t } = useTranslation()
  
  // Support backward compatibility while transitioning to new prop name
  const accessoryToUse = labelAccessory || rightAccessory

  const getValidationRules = () => {
    if (!isNumberInput) return rules;
    
    const numberRules = {
      required: required,
      pattern: {
        value: allowDecimals ? /^\d*\.?\d*$/ : /^\d*$/,
        message: 'invalidNumber'
      },
      validate: value => (!value || !isNaN(parseFloat(value))) || 'invalidNumber'
    }
    
    if (min !== undefined) numberRules.min = min
    if (max !== undefined) numberRules.max = max
    
    return numberRules;
  }

  const webTestIdProps = testID && Platform.OS === 'web' ? { dataSet: { testid: testID } } : {}

  const content = (
    <>
      {label && (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text category='label' style={{ marginTop: 15, marginBottom: 5 }}>
            {label}
          </Text>
          {accessoryToUse && accessoryToUse()}
        </View>
      )}
      <Controller
        name={name}
        control={control}
        rules={getValidationRules()}
        defaultValue={isNumberInput ? defaultValue : undefined}
        render={({field: { onChange, onBlur, value }, fieldState: { error }}) => (
          <>
            {isNumberInput ? (
              <StyledTextInput
                value={value}
                onChangeText={text => onChange(allowDecimals ? text.replace(/,/g, '.') : text)}
                onBlur={onBlur}
                keyboardType='decimal-pad'
                style={style}
                returnKeyType={returnKeyType}
                placeholder={placeholder}
                controlledInput
                {...props}
              />
            ) : (
              <Input
                value={value}
                onChangeText={(text) => {
                  // Apply text transform if specified
                  const transformedText = textTransform ? textTransform(text) : text
                  onChange(transformedText)
                  if (customOnChange) customOnChange(transformedText)
                }}
                onBlur={onBlur}
                style={style}
                keyboardType={keyboardType}
                returnKeyType={returnKeyType}
                autoCapitalize={autoCapitalize}
                placeholder={placeholder}
                {...props}
              />
            )}

            {error?.type === 'required' && (
              <Text status='danger' style={{ marginTop: 5, marginBottom: 15 }}>
                {t('fieldIsRequiredError')}
              </Text>
            )}
            {isNumberInput && (error?.type === 'pattern' || error?.type === 'validate') && (
              <Text status='danger' style={{ marginTop: 5, marginBottom: 15 }}>
                {t('invalidNumberError')}
              </Text>
            )}
            {isNumberInput && (error?.type === 'min' || error?.type === 'max') && (
              <Text status='danger' style={{ marginTop: 5, marginBottom: 15 }}>
                {t('numberRangeError', { min, max })}
              </Text>
            )}
          </>
        )}
      />
    </>
  )

  if (testID) {
    return <View testID={testID} {...webTestIdProps}>{content}</View>
  }
  return content
} 